<template>
  <section v-if="topic && Object.keys(topic).length" :class="['container', $style.topic]">
    <header :class="$style.header">
      <nav>
        <BackLink
          component="TeachingMaterialsTopic"
          group="teaching_materials"
          :label="topic.packageParent.title"
          name="TeachingMaterialsPackage"
          :params="{ slug: topic.packageParent.slug }"
        ></BackLink>
      </nav>
      <h1 v-html="topic.title"></h1>
      <div v-for="(artist, key) in topic.tmArtistInfo" :key="key">
        <div :class="$style.pronounce">
          <ProcessContent
            v-if="artist.audio_file"
            :content="artist.audio_file"
            :class="$style.audio"
          ></ProcessContent>
          <p v-if="artist.pronounciation_guide" v-html="artist.pronounciation_guide"></p>
        </div>
        <p v-if="artist.bio" :class="$style.bio" class="space-8-10-above" v-html="artist.bio"></p>
      </div>
      <p :class="$style.label">What’s Included</p>
      <p :class="$style.included">{{ getWhatsIncluded }}</p>
      <div v-if="topic.tmTopicPageElements && topic.tmTopicPageElements.pdf">
        <a :href="topic.tmTopicPageElements.pdf" :class="$style.pdf">Download PDF</a>
      </div>
      <div :class="$style.mst">
        <div v-if="topic.teachingMaterialMedia.nodes.length">
          <p :class="$style.label">Mediums</p>
          <ul>
            <li
              v-for="(medium, key) in topic.teachingMaterialMedia.nodes"
              :key="key"
              :class="{ comma: key < topic.teachingMaterialMedia.nodes.length - 1 }"
            >
              {{ medium.name }}
            </li>
          </ul>
        </div>
        <div v-if="topic.teachingMaterialSubjects.nodes || topic.teachingMaterialThemes.nodes">
          <p :class="$style.label">Subjects and Themes</p>
          <ul>
            <li
              v-for="(item, key) in getSubjectsAndThemes"
              :key="key"
              :class="{ comma: key < getSubjectsAndThemes.length - 1 }"
            >
              {{ item.name }}
            </li>
          </ul>
        </div>
      </div>
    </header>
    <section :class="[{ [$style.showGallery]: isGallery }, $style.gallery, 'space-12-16-above']">
      <ProcessContent
        v-if="topic.tmGallery"
        :content="topic.tmGallery"
        :no-wrapper="true"
      ></ProcessContent>
    </section>
    <ProcessContent
      v-if="topic.content"
      :content="topic.content"
      wrapper="section"
      :class="$style.content"
    ></ProcessContent>
    <section v-if="findData('view_discuss').length" :class="$style.viewDiscuss">
      <h2>View and Discuss</h2>
      <article v-for="(item, key) in findData('view_discuss')[0].content.slice(0, 2)" :key="key">
        <h3 v-html="item.title"></h3>
        <button
          v-for="(link, index) in item.links"
          :key="index"
          :class="[$style.links, moreLink]"
          @click.prevent="openLink(link)"
          @keypress.enter="openLink(link)"
          v-html="getLinkLabel(link)"
        ></button>
        <ProcessContent
          v-if="item.text"
          :content="item.text"
          class="space-8-10-above"
        ></ProcessContent>
        <div v-if="item.links"></div>
      </article>
      <TeachingMaterialsDrawer
        v-if="findData('view_discuss')[0].content.length > 2"
        :content="
          findData('view_discuss')[0].content.slice(2, findData('view_discuss')[0].content.length)
        "
        group="view-and-discuss"
        title="Show All"
        @link-opened="openLink"
      ></TeachingMaterialsDrawer>
    </section>
    <section v-if="findData('classroom_activity').length" :class="$style.activities">
      <h2>Classroom Activities</h2>
      <article
        v-for="(item, key) in findData('classroom_activity')[0].content.slice(0, 3)"
        :key="key"
      >
        <h3 v-html="item.title"></h3>
        <ProcessContent v-if="item.text" :content="item.text"></ProcessContent>
      </article>
      <TeachingMaterialsDrawer
        v-if="findData('classroom_activity')[0].content.length > 3"
        :content="
          findData('classroom_activity')[0].content.slice(
            3,
            findData('classroom_activity')[0].content.length,
          )
        "
        group="classroom-activities"
        title="Show All"
      ></TeachingMaterialsDrawer>
    </section>
    <section v-if="findData('context').length" :class="$style.context">
      <h2>Context</h2>
      <article v-for="(item, key) in findData('context')[0].content" :key="key">
        <h3 v-html="item.title"></h3>
        <ProcessContent v-if="item.text" :content="item.text"></ProcessContent>
      </article>
    </section>
    <section v-if="findInfo('key_terms', 'topic', instance).length" :class="$style.terms">
      <h2
        v-if="topic.tmTopicPageElements.key_terms.header.length"
        v-html="topic.tmTopicPageElements.key_terms.header"
      ></h2>
      <h2 v-else>Key Terms</h2>
      <div :class="term">
        <article
          v-for="(item, key) in findInfo('key_terms', 'topic', instance).slice(0, 4)"
          :key="key"
          class="space-12-16-above"
        >
          <h3 v-html="item.title"></h3>
          <p v-html="item.term"></p>
        </article>
      </div>
      <TeachingMaterialsDrawer
        v-if="findInfo('key_terms', 'topic', instance).length > 4"
        :content="
          findInfo('key_terms', 'topic', instance).slice(
            4,
            findInfo('key_terms', 'topic', instance).length,
          )
        "
        group="key-terms"
        title="Show All"
      ></TeachingMaterialsDrawer>
    </section>
    <section v-if="findInfo('all_resources', 'topic', instance).length" :class="$style.resources">
      <h2
        v-if="topic.tmTopicPageElements.all_resources.header.length"
        v-html="topic.tmTopicPageElements.all_resources.header"
      ></h2>
      <h2 v-else>Resources</h2>
      <ProcessContent
        :content="findInfo('all_resources', 'topic', instance)"
        wrapper="article"
      ></ProcessContent>
    </section>
    <section
      v-if="isArtist && topic.relatedMaterials && topic.relatedMaterials.length"
      :class="$style.related"
    >
      <h2>Related Materials</h2>
      <TeachingMaterialsFeatureShowcase
        :associated="topic.relatedMaterials"
      ></TeachingMaterialsFeatureShowcase>
    </section>
    <section v-else-if="!isArtist && getAssociatedTopics.length" :class="$style.related">
      <h2>More Topics: Discuss and Create</h2>
      <TeachingMaterialsFeatureShowcase
        :associated="getAssociatedTopics"
        associated-type="topics"
      ></TeachingMaterialsFeatureShowcase>
    </section>
    <p v-if="isFreeman" :class="[$style.freeman, 'space-48-64-above']">
      These teaching materials are supported by The Freeman Foundation.
    </p>
  </section>
</template>

<script setup lang="ts">
import BackLink from '@/components/common/BackLink.vue';
import { moreLink } from '@/styles/links.module.scss';
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { dataLoaded, useMetaData } from '@/composables/Common.js';
import { Link, TMData, TMTopic } from '@/types/teachingMaterials.interface';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { storeToRefs } from 'pinia';
import TeachingMaterialsDrawer from '@/components/teachingMaterials/TeachingMaterialsDrawer.vue';
import TeachingMaterialsFeatureShowcase from '@/components/teachingMaterials/TeachingMaterialsFeatureShowcase.vue';
import { teachingMaterialsTopicQuery } from '@/queries/GetTeachingMaterialsTopic';
import { term } from '@/styles/teachingMaterials.module.scss';
import { useEventStore } from '@/stores/eventStore.js';
import { useQuery } from 'villus';
import { useRoute, useRouter } from 'vue-router';
import { useTeachingMaterials } from '@/composables/TeachingMaterials.js';

const props = defineProps<{
  parent: string;
  slug: string;
}>();

const instance = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const store = useEventStore();
const { clicked, payload } = storeToRefs(store);

const { findInfo } = useTeachingMaterials();
const { setMetaDescription, setMetaTitle } = useMetaData();

const isGallery = ref(false);
const topic = ref<TMTopic>({});

defineExpose({ topic });

const getAssociatedTopics = computed(() => {
  if (topic.value.associatedTopics && topic.value.associatedTopics.length) {
    return topic.value.associatedTopics.filter(
      (item) => item.topic.databaseId !== topic.value.databaseId,
    );
  }

  return null;
});
const getSubjectsAndThemes = computed(() => {
  return [
    ...topic.value.teachingMaterialSubjects.nodes,
    ...topic.value.teachingMaterialThemes.nodes,
  ];
});
const getWhatsIncluded = computed(() => {
  const included = [] as Array<string>;

  if (topic.value.videoCount) {
    included.push(`${topic.value.videoCount} videos`);
  }

  if (topic.value.imageCount) {
    included.push(`${topic.value.imageCount} ${topic.value.imageCount > 1 ? 'images' : 'image'}`);
  }

  if (topic.value.vdCount) {
    included.push(`${topic.value.vdCount} view and discuss`);
  }

  if (topic.value.caCount) {
    included.push(
      `${topic.value.caCount} classroom ${topic.value.caCount > 1 ? 'activities' : 'activity'}`,
    );
  }

  const last = included.pop();
  const final = included.join(', ');

  return `${final}, and ${last}`;
});
const isArtist = computed(() => {
  return topic.value.tmType === 'artist';
});
const isFreeman = computed(() => {
  return props.parent === 'teaching-modern-and-contemporary-asian-art';
});

const { data } = useQuery({
  onData: (data) => {
    if (data.tmTopics && data.tmTopics.nodes.length) {
      topic.value = data.tmTopics.nodes[0];
      setMetaTitle(data.tmTopics.nodes[0].title);
      setMetaDescription(data.tmTopics.nodes[0].excerpt);
      dataLoaded();
    }

    if (!data.tmTopics || data.tmTopics.nodes.length === 0) {
      handleError('TeachingMaterialsTopic', props.slug);
    }
  },
  query: teachingMaterialsTopicQuery,
  variables: { slug: props.slug },
});

onMounted(() => {
  if (route.query && route.query.gallery) {
    isGallery.value = true;
  }
});

watch(clicked, (newValue) => {
  if (newValue && Object.keys(payload).length) {
    openLink(payload);
  }
});

function findData(key: string): TMData {
  if (topic.value.tmData && topic.value.tmData.length) {
    return topic.value.tmData.filter(
      (data) => data.type === key && data.content && data.content.length,
    );
  }

  return [];
}

function getLinkLabel(link: Link): string {
  if (link.label) {
    return link.label;
  }

  return link.video ? 'View video' : 'View artwork';
}

function openLink(link: Link): void {
  const value = link.video || link.image;

  router.push({
    path: route.path,
    query: { gallery: value },
  });
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: teachingMaterialsTopicQuery,
          variables: {
            slug: params.slug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.audio {
  button {
    height: 1.5rem;
  }

  svg {
    cursor: pointer;
    height: 1.5rem;
    width: 1.5rem;

    &:hover {
      path {
        fill: $black-hover;
      }
    }
  }

  time {
    display: none;
  }
}

.bio {
  @include heading-4;

  & {
    margin-top: 0.5rem;
  }
}

.content,
.view-discuss,
.activities,
.context {
  img {
    max-width: 100%;
  }
}

.freeman {
  @include body-2;

  & {
    margin-bottom: 0;
  }
}

.header {
  a[href$='.pdf'][href$='.pdf'] {
    margin-bottom: 0;
    margin-top: 2rem;
  }
  h1 + p {
    margin-top: 0;
  }
}

.included {
  margin-top: 0;
}

.label {
  @include heading-4;
}

.links {
  cursor: pointer;
  display: inline-block;
  text-decoration: none;
}

.mst {
  div {
    margin-right: 2rem;

    &:last-of-type {
      margin-top: 1rem;
    }
  }

  ul {
    display: inline-flex;
    flex-wrap: wrap;
    margin: 0; // defeat global
    padding-left: 0;

    li {
      list-style: none; // defeat global
      margin-top: 0;
    }
  }
}

.pdf {
  margin-bottom: 0;

  svg {
    height: 1rem;
    margin-left: 0.5rem;
    vertical-align: text-bottom;
    width: 1rem;
  }

  &:hover {
    background: $white-hover;
    border: 2px solid $black-hover;
    color: $white;

    svg {
      path {
        stroke: $white;
      }
    }
  }
}

.pronounce {
  display: flex;

  p {
    @include heading-4;

    & {
      margin-left: 0.5rem;
      margin-top: 0;
    }
  }
}

.resources {
  h3:not(:first-of-type) {
    margin-top: 1rem;
  }
}

.show-gallery {
  visibility: visible;
}

.topic {
  h2,
  h3 {
    margin: 0; // defeat global
  }

  img + p:global(.caption) {
    margin-top: 0;
  }

  p:global(.caption) {
    img {
      margin-bottom: 0.5rem;
    }
  }

  > section:not(:first-of-type) {
    @include space-48-64-above;
  }

  :global(.epigraph-author) {
    margin-bottom: 0;
  }
}

:global(.epigraph) {
  p {
    &::before {
      margin-left: 0;
      position: relative;
    }
  }
}

@media (min-width: $break-point) {
  .pronounce {
    p {
      margin-top: 0;
    }
  }
  .topic {
    @include grid;

    > * {
      grid-column: 1 / span 8;
    }

    .header {
      @include grid;

      & {
        grid-column: span 12;
      }

      > * {
        grid-column: 1 / span 8;
      }

      nav {
        grid-column: span 12;
      }
    }

    .gallery,
    .related {
      grid-column: 1 / span 8;
    }
  }

  :global(.epigraph) {
    p::before {
      margin-left: -1.35rem;
      position: absolute;
    }
  }
}
</style>
